import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import i18n from '../../i18n';

// --- Internal shell ---

interface ModalShellProps {
  title: string;
  description?: string;
  body?: React.ReactNode;
  size?: ModalSize;
  children: React.ReactNode;
  onDismiss: () => void;
}

export type ModalSize = 'default' | 'wide';

const sizeClass: Record<ModalSize, string> = {
  default: 'max-w-md',
  wide: 'max-w-3xl',
};

function ModalShell({ title, description, body, size = 'default', children, onDismiss }: ModalShellProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      onClose={onDismiss}
      onClick={(e) => {
        if (e.target === e.currentTarget) e.currentTarget.close();
      }}
      className={`m-auto max-h-[85vh] w-full ${sizeClass[size]} rounded-lg border border-border bg-surface p-0 shadow-xl backdrop:bg-black/50`}
    >
      <div className="flex max-h-[85vh] flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-heading font-semibold tracking-normal text-foreground">{title}</h2>
          <button
            type="button"
            aria-label={i18n.t('common.close')}
            onClick={() => ref.current?.close()}
            className="rounded-sm p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <XMarkIcon className="size-4" aria-hidden="true" />
          </button>
        </div>
        {description ? (
          <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">{description}</p>
        ) : null}
        {/* Scrolls on its own so the title and the actions stay in view. */}
        {body ? <div className="min-h-0 flex-1 overflow-y-auto">{body}</div> : null}
        <div className="flex justify-end gap-2 pt-2">{children}</div>
      </div>
    </dialog>
  );
}

// --- Mount helper ---

function mountModal(content: (cleanup: () => void) => React.ReactNode): void {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  const cleanup = () => {
    setTimeout(() => {
      root.unmount();
      container.remove();
    }, 0);
  };

  root.render(<>{content(cleanup)}</>);
}

// --- Public API ---

export interface ShowModalOptions {
  title: string;
  description?: string;
  closeLabel?: string;
}

export interface ShowConfirmModalOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
}

const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60';

const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted';

const dangerButton =
  'inline-flex items-center justify-center gap-2 rounded bg-destructive px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-destructive disabled:cursor-not-allowed disabled:opacity-60';

export function showModal({ title, description, closeLabel }: ShowModalOptions): void {
  const resolvedCloseLabel = closeLabel ?? i18n.t('common.ok');
  mountModal((cleanup) => (
    <ModalShell title={title} description={description} onDismiss={cleanup}>
      <button type="button" onClick={cleanup} className={primaryButton}>
        {resolvedCloseLabel}
      </button>
    </ModalShell>
  ));
}

export interface ShowContentModalOptions {
  title: string;
  description?: string;
  /** Rendered by React, so it may be a component that fetches its own data. A function
   * form receives `close`, for content (e.g. a picker) that should dismiss itself on choice. */
  body: React.ReactNode | ((close: () => void) => React.ReactNode);
  closeLabel?: string;
  size?: ModalSize;
}

/** A read-only modal whose body is arbitrary content rather than a message. */
export function showContentModal({
  title,
  description,
  body,
  closeLabel,
  size = 'default',
}: ShowContentModalOptions): void {
  const resolvedCloseLabel = closeLabel ?? i18n.t('common.close');
  mountModal((cleanup) => (
    <ModalShell title={title} description={description} body={typeof body === 'function' ? body(cleanup) : body} size={size} onDismiss={cleanup}>
      <button type="button" onClick={cleanup} className={secondaryButton}>
        {resolvedCloseLabel}
      </button>
    </ModalShell>
  ));
}

export function showConfirmModal({
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
}: ShowConfirmModalOptions): Promise<boolean> {
  const resolvedConfirmLabel = confirmLabel ?? i18n.t('common.confirm');
  const resolvedCancelLabel = cancelLabel ?? i18n.t('common.cancel');
  return new Promise((resolve) => {
    mountModal((cleanup) => {
      const settled = { current: false };

      const handleResult = (result: boolean) => {
        if (settled.current) return;
        settled.current = true;
        cleanup();
        resolve(result);
      };

      return (
        <ModalShell title={title} description={description} onDismiss={() => handleResult(false)}>
          <button
            type="button"
            onClick={() => handleResult(false)}
            className={secondaryButton}
          >
            {resolvedCancelLabel}
          </button>
          <button
            type="button"
            onClick={() => handleResult(true)}
            className={variant === 'danger' ? dangerButton : primaryButton}
          >
            {resolvedConfirmLabel}
          </button>
        </ModalShell>
      );
    });
  });
}

export interface ShowTypedConfirmModalOptions {
  title: string;
  description?: string;
  /** The user must type this exactly before the confirm button enables. */
  confirmationText: string;
  label: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/** Destructive actions that are hard to undo, per UX-IA section 6. */
export function showTypedConfirmModal({
  title,
  description,
  confirmationText,
  label,
  confirmLabel,
  cancelLabel,
}: ShowTypedConfirmModalOptions): Promise<boolean> {
  const resolvedConfirmLabel = confirmLabel ?? i18n.t('common.confirm');
  const resolvedCancelLabel = cancelLabel ?? i18n.t('common.cancel');

  return new Promise((resolve) => {
    mountModal((cleanup) => {
      const settled = { current: false };

      const handleResult = (result: boolean) => {
        if (settled.current) return;
        settled.current = true;
        cleanup();
        resolve(result);
      };

      return (
        <TypedConfirmContent
          title={title}
          description={description}
          confirmationText={confirmationText}
          label={label}
          confirmLabel={resolvedConfirmLabel}
          cancelLabel={resolvedCancelLabel}
          onResult={handleResult}
        />
      );
    });
  });
}

function TypedConfirmContent({
  title,
  description,
  confirmationText,
  label,
  confirmLabel,
  cancelLabel,
  onResult,
}: {
  title: string;
  description?: string;
  confirmationText: string;
  label: string;
  confirmLabel: string;
  cancelLabel: string;
  onResult: (result: boolean) => void;
}) {
  const [value, setValue] = useState('');
  const matches = value.trim() === confirmationText;

  return (
    <ModalShell
      title={title}
      description={description}
      onDismiss={() => onResult(false)}
      body={
        <label className="block text-sm">
          <span className="font-medium text-foreground">{label}</span>
          <input
            type="text"
            value={value}
            autoComplete="off"
            onChange={(event) => setValue(event.target.value)}
            className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          />
        </label>
      }
    >
      <button type="button" onClick={() => onResult(false)} className={secondaryButton}>
        {cancelLabel}
      </button>
      <button type="button" disabled={!matches} onClick={() => onResult(true)} className={dangerButton}>
        {confirmLabel}
      </button>
    </ModalShell>
  );
}
